import { useEffect } from "react";
import { useDispatch } from "react-redux";
import pollResultsSocket from "../serviceAPI/pollingSocket";
import {
  fetchPollResults,
  receivePollResultsFromSocket,
  setPollResultsSocketStatus,
} from "../slices/PollingSlice";

/** REST sync keeps all clients aligned without Redis (shared MongoDB source of truth). */
const REST_SYNC_MS = 3000;

export function normalizePollStatusForSocket(status) {
  return String(status ?? "draft").trim().toLowerCase();
}

/**
 * Live poll results only: Socket.IO plus periodic REST refresh while status is live.
 * Use server-backed status (e.g. pollBaselineStatus), not unsaved form state.
 */
export function usePollResultsSocket({ visitId, pollId, pollStatus }) {
  const dispatch = useDispatch();
  const normalizedStatus = normalizePollStatusForSocket(pollStatus);
  const shouldSubscribe = Boolean(
    visitId && pollId && normalizedStatus === "live"
  );

  useEffect(() => {
    if (!shouldSubscribe) {
      pollResultsSocket.disconnect();
      dispatch(setPollResultsSocketStatus("idle"));
      return undefined;
    }

    dispatch(fetchPollResults({ visitId, pollId }));

    const unsubscribeResults = pollResultsSocket.onResults((summary) => {
      dispatch(receivePollResultsFromSocket(summary));
    });

    const unsubscribeStatus = pollResultsSocket.onStatus((status) => {
      dispatch(setPollResultsSocketStatus(status));
    });

    pollResultsSocket.subscribe(visitId, pollId);

    const intervalId = window.setInterval(() => {
      dispatch(fetchPollResults({ visitId, pollId }));
    }, REST_SYNC_MS);

    return () => {
      window.clearInterval(intervalId);
      unsubscribeResults();
      unsubscribeStatus();
      pollResultsSocket.disconnect();
      dispatch(setPollResultsSocketStatus("idle"));
    };
  }, [shouldSubscribe, visitId, pollId, normalizedStatus, dispatch]);
}
